import React from "react";
import { isStale, safeHttpUrl } from "../utils";
import {
	CheckSquare,
	Bot,
	Ban,
	Scale,
	Eraser,
	GitPullRequestDraft,
	Info,
	MessageSquareMore,
	MessageSquareReply,
	TriangleAlert,
	Moon,
} from "lucide-react";
import { Tooltip } from "./Tooltip";

export const MRStatusIcons = ({ mr, iconClass = "w-4 h-4" }) => {
	if (!mr) return null;

	let mrSizeIcon = <></>;
	if (mr.diffStatsSummary) {
		const changedLines =
			mr.diffStatsSummary.additions + mr.diffStatsSummary.changes;
		if (changedLines > 5000) {
			mrSizeIcon = (
				<Tooltip content="Large pull request - over 5000 lines changed">
					<Scale
						className={`${iconClass} text-amber-500 monokai:text-monokai-orange dracula:text-dracula-orange nord:text-nord-orange`}
					/>
				</Tooltip>
			);
		} else if (mr.diffStatsSummary.deletions > 2 * changedLines) {
			mrSizeIcon = (
				<Tooltip content="Cleanup MR - primarily removing or replacing code">
					<Eraser
						className={`${iconClass} text-blue-500 monokai:text-monokai-blue dracula:text-dracula-cyan nord:text-nord-brightBlue`}
					/>
				</Tooltip>
			);
		}
	} else {
		mrSizeIcon = (
			<Tooltip content="Could not retrieve size of this MR">
				<Info
					className={`${iconClass} text-amber-500 monokai:text-monokai-blue dracula:text-dracula-cyan nord:text-nord-brightBlue`}
				/>
			</Tooltip>
		);
	}

	return (
		<>
			{mrSizeIcon}

			{isStale(mr) && (
				<Tooltip content="Stale MR - no activity for over 14 days">
					<Moon
						className={`${iconClass} text-indigo-500 monokai:text-monokai-purple dracula:text-dracula-purple nord:text-nord-purple`}
					/>
				</Tooltip>
			)}

			{mr.conflicts && (
				<Tooltip content="Conflicts exist between the source and target branches">
					<Ban
						className={`${iconClass} text-red-500 monokai:text-monokai-red dracula:text-dracula-red nord:text-nord-red`}
					/>
				</Tooltip>
			)}

			{mr.additionalRules?.blocking?.length > 0 && (
				<Tooltip
					content="Project rules may prevent this change being reviewed/merged"
					listItems={mr.additionalRules.blocking}
				>
					<span className="inline-flex items-center text-red-500">
						<TriangleAlert className={iconClass} />
						<span className="ml-1 text-xs font-medium">
							{mr.additionalRules.blocking.length}
						</span>
					</span>
				</Tooltip>
			)}

			{mr.additionalRules?.info?.length > 0 && (
				<Tooltip
					content="Project rules may prevent this change being reviewed/merged"
					listItems={mr.additionalRules.info}
				>
					<span className="inline-flex items-center text-blue-500">
						<Info className={iconClass} />
						<span className="ml-1 text-xs font-medium">
							{mr.additionalRules.info.length}
						</span>
					</span>
				</Tooltip>
			)}

			{mr.discussions > 0 && (
				<Tooltip content="Blocking discussions">
					<span className="inline-flex items-center text-yellow-500 monokai:text-monokai-yellow dracula:text-dracula-yellow nord:text-nord-orange">
						<CheckSquare className={iconClass} />
						<span className="ml-1 text-xs font-medium">
							{mr.discussions}
						</span>
					</span>
				</Tooltip>
			)}

			<ReviewerThreadsIcon threads={mr.reviewerThreads} iconClass={iconClass} />
		</>
	);
};

// Threads the viewer opened on someone else's MR: highlighted and linked to
// the newest reply once anyone answers, muted while still awaiting a reply.
export const ReviewerThreadsIcon = ({ threads, iconClass = "w-4 h-4" }) => {
	if (!threads) return null;

	const { started, replied, replies } = threads;
	const threadNoun = started === 1 ? "thread" : "threads";

	if (replied === 0) {
		return (
			<Tooltip
				content={`You opened ${started} unresolved ${threadNoun}, awaiting a reply`}
			>
				<span className="inline-flex items-center text-[var(--color-text-tertiary)]">
					<MessageSquareMore className={iconClass} />
					<span className="ml-1 text-xs font-medium">{started}</span>
				</span>
			</Tooltip>
		);
	}

	const responders = [...new Set(replies.map((reply) => reply.author.name))];
	const href = safeHttpUrl(replies[0]?.url);
	const content = (
		<>
			<MessageSquareReply className={iconClass} />
			<span className="ml-1 text-xs font-medium">{replied}</span>
		</>
	);
	const className =
		"inline-flex items-center text-sky-700 dark:text-sky-400 monokai:text-monokai-blue dracula:text-dracula-cyan nord:text-nord-brightBlue";

	return (
		<Tooltip
			content={`${replied} of your ${started} unresolved ${threadNoun} answered by ${responders.join(", ")}`}
		>
			{href ? (
				<a
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					draggable="false"
					onClick={(event) => event.stopPropagation()}
					aria-label="Open the latest reply to your thread"
					className={`${className} hover:underline`}
				>
					{content}
				</a>
			) : (
				<span className={className}>{content}</span>
			)}
		</Tooltip>
	);
};

export const BotIcon = ({ author, iconClass = "w-4 h-4" }) => {
	if (!author?.bot) return null;
	
	return (
		<Tooltip content="Bot!">
			<Bot
				className={`${iconClass} ml-1 text-[var(--color-text-tertiary)]`}
			/>
		</Tooltip>
	);
};

export const DraftIcon = ({ draft, iconClass = "w-4 h-4" }) => {
	if (!draft) return null;
	
	return (
		<Tooltip content="Draft pull request">
			<GitPullRequestDraft
				className={`${iconClass} text-[var(--color-text-tertiary)]`}
			/>
		</Tooltip>
	);
};
